import type { PublicationItem, PublicationsResult } from 'components/discover/publications/model';
import type { SeeMorePublicationResult } from 'components/discover/publications/see-more/model';
import type { MostViewedResearchItem } from 'components/discover/research/model';
import type { TopTradeItem, TopTradesResult } from 'components/discover/top-trades/model';
import type { Observable } from 'rxjs';
import { map, timeout } from 'rxjs/operators';
import type { IHttpClient } from 'utils/http/httpClient.model';

export interface ProductsApi {
  fetchProducts(region: string): Observable<PublicationItem[]>;
}

export interface MoreProductsApi {
  fetchMoreProducts(region: string): Observable<SeeMorePublicationResult>;
}

export interface ThematicsApi {
  fetchThematics(region: string): Observable<PublicationItem[]>;
}

export interface MoreThematicsApi {
  fetchMoreThematics(region: string): Observable<SeeMorePublicationResult>;
}

export interface VideosApi {
  fetchVideos(region: string): Observable<PublicationItem[]>;
}

export interface MoreVideosApi {
  fetchMoreVideos(region: string): Observable<SeeMorePublicationResult>;
}

export interface TopTradesApi {
  fetchTopTrades(region: string): Observable<TopTradeItem[]>;
}

export interface PopularResearchApi {
  fetchPopularResearch(): Observable<MostViewedResearchItem[]>;
}

export function publicationConnector(
  httpClient: IHttpClient,
  timeoutInMillis = 30000,
): ThematicsApi &
  ProductsApi &
  VideosApi &
  TopTradesApi &
  MoreProductsApi &
  MoreThematicsApi &
  MoreVideosApi &
  PopularResearchApi {
  return {
    fetchThematics(region: string): Observable<PublicationItem[]> {
      return httpClient
        .getJSON<PublicationsResult>({
          url: 'api/v1/thematics',
          params: { region },
          targetHost: 'AdvisoryBridge',
        })
        .pipe(
          timeout(timeoutInMillis),
          map(r => r.payload.items),
        );
    },
    fetchProducts(region: string): Observable<PublicationItem[]> {
      return httpClient
        .getJSON<PublicationsResult>({
          url: 'api/v1/products',
          params: { region },
          targetHost: 'AdvisoryBridge',
        })
        .pipe(
          timeout(timeoutInMillis),
          map(r => r.payload.items),
        );
    },
    fetchVideos(region: string): Observable<PublicationItem[]> {
      return httpClient
        .getJSON<PublicationsResult>({
          url: 'api/v1/videos',
          params: { region },
          targetHost: 'AdvisoryBridge',
        })
        .pipe(
          timeout(timeoutInMillis),
          map(r => r.payload.items),
        );
    },
    fetchMoreThematics(region: string): Observable<SeeMorePublicationResult> {
      return httpClient
        .getJSON<SeeMorePublicationResult>({
          url: 'api/v1/thematics/more',
          params: { region },
          targetHost: 'AdvisoryBridge',
        })
        .pipe(
          timeout(timeoutInMillis),
          map(r => r.payload),
        );
    },
    fetchMoreProducts(region: string): Observable<SeeMorePublicationResult> {
      return httpClient
        .getJSON<SeeMorePublicationResult>({
          url: 'api/v1/products/more',
          params: { region },
          targetHost: 'AdvisoryBridge',
        })
        .pipe(
          timeout(timeoutInMillis),
          map(r => r.payload),
        );
    },
    fetchMoreVideos(region: string): Observable<SeeMorePublicationResult> {
      return httpClient
        .getJSON<SeeMorePublicationResult>({
          url: 'api/v1/videos/more',
          params: { region },
          targetHost: 'AdvisoryBridge',
        })
        .pipe(
          timeout(timeoutInMillis),
          map(r => r.payload),
        );
    },
    fetchTopTrades(region: string): Observable<TopTradeItem[]> {
      return httpClient
        .getJSON<TopTradesResult>({
          url: 'api/v1/top-trades',
          params: { region },
          targetHost: 'AdvisoryBridge',
        })
        .pipe(
          timeout(timeoutInMillis),
          map(r => r.payload.items),
        );
    },
    fetchPopularResearch(): Observable<MostViewedResearchItem[]> {
      return httpClient
        .getJSON<{ items: MostViewedResearchItem[] }>({
          url: 'api/v1/mostViewed',
          targetHost: 'AdvisoryBridge',
        })
        .pipe(
          timeout(timeoutInMillis),
          map(r => r.payload.items),
        );
    },
  };
}
